import { csrfToken } from './api'
import type { ChatMessage } from './types'

export type ChatMessageRating = 1 | -1

export interface AgentChatLogMessage {
  client_message_id: string
  role: ChatMessage["role"]
  text: string
  position: number
  from_voice?: boolean
  client_timestamp?: string
}

export interface AgentChatSessionSummary {
  id: number
  created_at: string
  updated_at: string
  provider?: string | null
  model?: string | null
  key_source?: string | null
  message_count: number
  preview?: string | null
}

export interface AgentChatSessionDetail extends AgentChatSessionSummary {
  messages: Array<{
    client_message_id: string
    role: ChatMessage["role"]
    text: string
    position: number
    from_voice?: boolean
    rating?: number | null
    client_timestamp?: string | null
  }>
}

const CHAT_ROLES: ChatMessage["role"][] = [
  "assistant",
  "user",
  "tool",
  "thinking",
  "plan",
]

/** List the current user's AEGIS chat sessions for a project (newest first). */
export async function listAgentChatSessions(
  projectId: number
): Promise<AgentChatSessionSummary[]> {
  const response = await fetch(`/projects/${projectId}/agent_chat_sessions`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(detail || `Failed to list chat sessions (${response.status})`)
  }
  const data = (await response.json()) as { sessions?: AgentChatSessionSummary[] }
  return Array.isArray(data.sessions) ? data.sessions : []
}

/** Load one of the current user's AEGIS chat sessions (with messages). */
export async function fetchAgentChatSession(
  projectId: number,
  sessionId: number
): Promise<AgentChatSessionDetail> {
  const response = await fetch(
    `/projects/${projectId}/agent_chat_sessions/${sessionId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }
  )
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(detail || `Failed to load chat session (${response.status})`)
  }
  return (await response.json()) as AgentChatSessionDetail
}

/** Map a persisted session payload into in-memory chat bubbles. */
export function messagesFromAgentChatSession(
  session: AgentChatSessionDetail
): ChatMessage[] {
  const sorted = [...(session.messages || [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
  return sorted
    .filter(message => CHAT_ROLES.includes(message.role))
    .map(message => {
      const rating =
        message.rating === 1 || message.rating === -1 ? message.rating : undefined
      return {
        id: message.client_message_id,
        role: message.role,
        text: message.text,
        timestamp: message.client_timestamp || "now",
        ...(message.from_voice ? { fromVoice: true } : {}),
        ...(rating ? { rating } : {}),
      }
    })
}

/** Create a new persisted AEGIS chat session for this project. */
export async function createAgentChatSession(
  projectId: number,
  meta: { provider?: string; model?: string; keySource?: string }
): Promise<number> {
  const response = await fetch(`/projects/${projectId}/agent_chat_sessions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify({
      provider: meta.provider,
      model: meta.model,
      key_source: meta.keySource,
    }),
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(detail || `Failed to create chat session (${response.status})`)
  }
  const data = (await response.json()) as { id?: number }
  if (typeof data.id !== "number") {
    throw new Error("Chat session response was missing id.")
  }
  return data.id
}

/** Upsert a batch of transcript messages into the active session. */
export async function appendAgentChatMessages(
  projectId: number,
  sessionId: number,
  messages: AgentChatLogMessage[]
): Promise<void> {
  if (messages.length === 0) return
  const response = await fetch(
    `/projects/${projectId}/agent_chat_sessions/${sessionId}/append_messages`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({ messages }),
    }
  )
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(detail || `Failed to append chat messages (${response.status})`)
  }
}

/** Persist a helpful / not-helpful rating for an assistant message. */
export async function rateAgentChatMessage(
  projectId: number,
  sessionId: number,
  clientMessageId: string,
  rating: ChatMessageRating | null
): Promise<ChatMessageRating | null> {
  const response = await fetch(
    `/projects/${projectId}/agent_chat_sessions/${sessionId}/rate_message`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({
        client_message_id: clientMessageId,
        rating,
      }),
    }
  )
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(detail || `Failed to rate message (${response.status})`)
  }
  const data = (await response.json()) as { rating?: number | null }
  if (data.rating === 1 || data.rating === -1) return data.rating
  return null
}

/** Compact elapsed-time label for the chat UI and transcript export. */
export function formatChatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0s"
  if (ms < 10_000) {
    const tenths = Math.floor(ms / 100) / 10
    return `${tenths.toFixed(1)}s`
  }
  const totalSec = Math.floor(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`
}

/** Build a plain-text transcript of the current in-memory chat. */
export function formatChatTranscriptTxt(
  messages: ChatMessage[],
  meta?: { projectName?: string; modelLabel?: string }
): string {
  const lines: string[] = []
  lines.push("AEGIS chat log")
  lines.push(`Exported: ${new Date().toISOString()}`)
  if (meta?.projectName) lines.push(`Project: ${meta.projectName}`)
  if (meta?.modelLabel) lines.push(`Model: ${meta.modelLabel}`)
  lines.push("")
  lines.push("─".repeat(48))
  lines.push("")

  for (const message of messages) {
    const role =
      message.role === "assistant"
        ? "AEGIS"
        : message.role === "user"
          ? "User"
          : message.role === "tool"
            ? "Tool"
            : message.role === "thinking"
              ? "Thinking"
              : message.role === "plan"
                ? "Plan"
                : message.role
    const stamp = message.timestamp && message.timestamp !== "now" ? ` (${message.timestamp})` : ""
    const voice = message.fromVoice ? " [voice]" : ""
    const rating =
      message.rating === 1 ? " [+]" : message.rating === -1 ? " [-]" : ""
    const duration =
      typeof message.durationMs === "number" ? ` [${formatChatDuration(message.durationMs)}]` : ""
    lines.push(`[${role}]${stamp}${voice}${rating}${duration}`)
    lines.push(message.text.trim() || "(empty)")
    lines.push("")
  }

  return lines.join("\n").trimEnd() + "\n"
}

/** Trigger a browser download of the current chat as a .txt file. */
export function downloadChatTranscriptTxt(
  messages: ChatMessage[],
  meta?: { projectName?: string; modelLabel?: string; filename?: string }
): void {
  const text = formatChatTranscriptTxt(messages, meta)
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
  a.href = url
  a.download = meta?.filename || `aegis-chat-${stamp}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function toAgentChatLogMessages(messages: ChatMessage[]): AgentChatLogMessage[] {
  return messages.map((message, index) => ({
    client_message_id: message.id,
    role: message.role,
    text: message.text,
    position: index,
    from_voice: !!message.fromVoice,
    client_timestamp: message.timestamp,
  }))
}
